import React from 'react';
import ProposalRow, { ProposalRowProps } from './ProposalRow';
import { gql, DocumentNode } from '@apollo/client';
import {
	AcceptProposalsModalContactFragment,
	AcceptProposalsModalContactForGetContractWhereFragment,
	ProposalStatus,
} from '#types';
import MuiModal, { MuiModalProps } from '../../../mui/MuiModal/MuiModal';
import { Divider, List } from '@mui/material';
import { Button } from '../../Button';
import { filterTypenameFromArray } from '../../../lib/helpers/apollo';
import { handleClose } from '../closeContractSignModalIfOpen';

// sometimes we need this on Proposals and sometimes on [Proposal!]
const acceptProposalsModalProposalFragment = gql`
	fragment acceptProposalsModalProposal on Proposal {
		id
		status
		...acceptProposalModalRowProposal
	}
	${ ProposalRow.fragments.acceptProposalModalRowProposalFragment }
`;

const acceptProposalsModalContactFragment = gql`
	fragment acceptProposalsModalContact on Contact {
		id
		customer {
			id
		}
		proposals {
			...acceptProposalsModalProposal
		}
	}
	${ acceptProposalsModalProposalFragment }
`;

// now for the Contract-specific contact type
const acceptProposalsModalContactForGetContractWhereFragment = gql`
	fragment acceptProposalsModalContactForGetContractWhere on ContactForGetContractWhere {
		id
		customer {
			id
		}
		proposals {
			...acceptProposalsModalProposal
		}
	}
	${ acceptProposalsModalProposalFragment }
`;

export type AcceptProposalsModalProps = {
	title?: string;
	description?: string;
	contact?:
	| AcceptProposalsModalContactFragment
	| AcceptProposalsModalContactForGetContractWhereFragment
	| null;
	excludedDocumentId: string;
} & Pick<MuiModalProps['muiModalProps'], 'open' | 'onClose'> &
Pick<ProposalRowProps, 'onNavigate'>;

type AcceptProposalsModalPropsWithFragment =
	React.FC<AcceptProposalsModalProps> & {
		fragments: {
			acceptProposalsModalContactFragment: DocumentNode;
			acceptProposalsModalProposalFragment: DocumentNode;
			acceptProposalsModalContactForGetContractWhereFragment: DocumentNode;
		};
	};

const AcceptProposalsModal: AcceptProposalsModalPropsWithFragment = ( {
	open,
	onClose,
	onNavigate,
	contact,
	title,
	description,
	excludedDocumentId,
} ) => {
	if ( !contact?.proposals ) {
		return null;
	}
	const proposals = filterTypenameFromArray( contact.proposals ).filter(
		( proposal ) => proposal.status === ProposalStatus.Sent
	);
	return (
		<MuiModal
			muiModalProps={ { open, onClose: handleClose( onClose ) } }
			title={ `${ title }! Next up...` }
			description={ description }
			controls={
				<Button variant='outlined' onClick={ handleClose( onClose ) }>
					Close
				</Button>
			}
		>
			<List disablePadding>
				{ proposals
					.filter( ( proposal ) => proposal.id !== excludedDocumentId )
					.map( ( proposal, index ) => (
						<React.Fragment key={ proposal.id }>
							<ProposalRow
								proposal={ proposal }
								onNavigate={ handleClose( onNavigate ) }
							/>
							{ index < proposals.length - 1 ? <Divider /> : null }
						</React.Fragment>
					) ) }
			</List>
		</MuiModal>
	);
};

AcceptProposalsModal.fragments = {
	acceptProposalsModalContactFragment,
	acceptProposalsModalProposalFragment,
	acceptProposalsModalContactForGetContractWhereFragment,
};

export default AcceptProposalsModal;
